import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { create_province, get_provinces } from '../../redux/actions/provinceAction'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

const CreateProvince = ({ onFormSuccess }) => {
  const dispatch = useDispatch()
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      rent_increase_frequency_days: 365,
      has_above_guideline_process: false,
      has_standard_lease_form: false,
      lease_form_mandatory: false,
      allows_electronic_service: false,
      requires_registered_mail: false,
      allows_in_person_service: true,
      requires_proof_of_service: true,
      has_online_dispute_portal: false,
      is_active: true
    }
  })

  const { isSuccess, isLoading, error } = useSelector(state => state.province)

  const submit = async (data) => {
    dispatch(create_province(data))
    if(isSuccess){
      reset();
      // Using a toast or custom modal is better than alert, but alert works for now
      // alert("Province created successfully!"); 
        dispatch(get_provinces()) 
        toast.success('Povince Added!');
      if (onFormSuccess) {
        onFormSuccess();
      }
    }
  }

 
  // Custom Styles for specific visual tweaks
  const styles = {
    sectionIcon: {
      backgroundColor: '#eef2ff',
      color: '#4f46e5',
      padding: '8px',
      borderRadius: '8px',
      marginRight: '12px'
    },
    card: {
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
      marginBottom: '1.5rem'
    },
    sectionTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '0'
    }
  };

  return (
    <div className="container-fluid px-1 py-2">
      
      {/* Header */}
      <div className="mb-4 text-center">
        <h2 className="fw-bold text-dark">New Province Configuration</h2>
        <p className="text-muted">Define regulatory rules, forms, and fees for a new jurisdiction.</p>
      </div>
      
      {error && (
        <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit(submit)}>
        
        {/* SECTION 1: Basic Identification */}
        <div className="card" style={styles.card}>
          <div className="card-body p-4">
            <h5 className="card-title mb-4" style={styles.sectionTitle}>
              <i className="bi bi-geo-alt-fill" style={styles.sectionIcon}></i>
              Basic Identification
            </h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="form-floating">
                  <input 
                    type="text" 
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    id="provName"
                    placeholder="Ontario"
                    {...register('name', { required: 'Name is required' })} 
                  />
                  <label htmlFor="provName">Province Name *</label>
                  <div className="invalid-feedback">{errors.name?.message}</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-floating">
                  <input 
                    type="text" 
                    className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                    id="provCode"
                    placeholder="ON"
                    {...register('code', { required: 'Code is required' })} 
                  />
                  <label htmlFor="provCode">Code (e.g., ON) *</label>
                  <div className="invalid-feedback">{errors.code?.message}</div>
                </div>
              </div>
              <div className="col-md-3 d-flex align-items-center justify-content-center">
                <div className="form-check form-switch p-3 bg-light rounded w-100 d-flex justify-content-between">
                  <label className="form-check-label fw-bold" htmlFor="isActive">Active Status</label>
                  <input className="form-check-input" type="checkbox" {...register('is_active')} id="isActive" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: Regulatory Body */}
        <div className="card" style={styles.card}>
          <div className="card-body p-4">
            <h5 className="card-title mb-4" style={styles.sectionTitle}>
              <i className="bi bi-bank2" style={styles.sectionIcon}></i>
              Regulatory Body & Legislation
            </h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="form-floating">
                  <input 
                    type="text" 
                    className={`form-control ${errors.regulatory_body_name ? 'is-invalid' : ''}`}
                    id="regBody"
                    placeholder="Board Name"
                    {...register('regulatory_body_name', { required: true })} 
                  />
                  <label htmlFor="regBody">Regulatory Body Name *</label>
                </div>
              </div>
              <div className="col-md-2">
                <div className="form-floating">
                  <input type="text" className="form-control" id="acronym" placeholder="LTB" {...register('regulatory_body_acronym')} />
                  <label htmlFor="acronym">Acronym</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-floating">
                  <input 
                    type="text" 
                    className={`form-control ${errors.key_legislation ? 'is-invalid' : ''}`}
                    id="legislation"
                    placeholder="Act Name"
                    {...register('key_legislation', { required: true })} 
                  />
                  <label htmlFor="legislation">Key Legislation *</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 3: Rent Increase Rules */}
        <div className="card" style={styles.card}>
          <div className="card-body p-4">
            <h5 className="card-title mb-4" style={styles.sectionTitle}>
              <i className="bi bi-graph-up-arrow" style={styles.sectionIcon}></i>
              Rent Increase Rules
            </h5>
            <div className="row g-3 align-items-center">
              <div className="col-md-3">
                <div className="input-group has-validation">
                  <div className="form-floating">
                    <input type="number" className="form-control" id="rentNotice" placeholder="90" {...register('rent_increase_notice_days', { required: true })} />
                    <label htmlFor="rentNotice">Notice Required *</label>
                  </div>
                  <span className="input-group-text bg-light">Days</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="input-group">
                  <div className="form-floating">
                    <input type="number" step="0.01" className="form-control" id="rentCap" placeholder="2.5" {...register('rent_increase_cap_percentage')} />
                    <label htmlFor="rentCap">Annual Cap</label>
                  </div>
                  <span className="input-group-text bg-light">%</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="input-group">
                  <div className="form-floating">
                    <input type="number" className="form-control" id="rentFreq" placeholder="365" {...register('rent_increase_frequency_days', { required: true })} />
                    <label htmlFor="rentFreq">Frequency *</label>
                  </div>
                  <span className="input-group-text bg-light">Days</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-floating">
                  <input type="text" className="form-control" id="rentForm" placeholder="N1" {...register('rent_increase_form_code')} />
                  <label htmlFor="rentForm">Form Code</label>
                </div>
              </div>
              
              <div className="col-12 mt-3">
                <div className="bg-light p-3 rounded d-flex align-items-center flex-wrap gap-3">
                    <div className="form-check form-switch me-4">
                      <input className="form-check-input" type="checkbox" {...register('has_above_guideline_process')} id="agp" />
                      <label className="form-check-label" htmlFor="agp">Allow Above Guideline Increase</label>
                    </div>
                    <div className="flex-grow-1" style={{maxWidth: '250px'}}>
                         <input type="text" className="form-control form-control-sm" placeholder="AGI Form Code (e.g. L5)" {...register('above_guideline_form_code')} />
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 4: Non-Payment & Eviction */}
        <div className="card" style={styles.card}>
          <div className="card-body p-4">
            <h5 className="card-title mb-4" style={styles.sectionTitle}>
              <i className="bi bi-file-earmark-x" style={styles.sectionIcon}></i>
              Non-Payment & Eviction
            </h5>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label text-muted small fw-bold text-uppercase">Timeline</label>
                <div className="input-group mb-2">
                   <span className="input-group-text">Notice</span>
                   <input type="number" className="form-control" placeholder="14" {...register('non_payment_notice_days', { required: true })} />
                   <span className="input-group-text">Days</span>
                </div>
                <div className="input-group">
                   <span className="input-group-text">Remedy</span>
                   <input type="number" className="form-control" placeholder="0" {...register('non_payment_remedy_days')} />
                   <span className="input-group-text">Days</span>
                </div>
              </div>

              <div className="col-md-4">
                <label className="form-label text-muted small fw-bold text-uppercase">Forms</label>
                 <div className="form-floating mb-2">
                    <input type="text" className="form-control" id="npForm" placeholder="N4" {...register('non_payment_form_code')} />
                    <label htmlFor="npForm">Non-Payment Form Code</label>
                 </div>
                 <div className="form-floating">
                    <input type="text" className="form-control" id="evictForm" placeholder="L1" {...register('eviction_application_form_code')} />
                    <label htmlFor="evictForm">Eviction App Form</label>
                 </div>
              </div>

              <div className="col-md-4">
                 <label className="form-label text-muted small fw-bold text-uppercase">Fees</label>
                 <div className="input-group">
                  <span className="input-group-text">$</span>
                  <div className="form-floating">
                    <input type="number" step="0.01" className="form-control" id="evictFee" placeholder="0.00" {...register('eviction_filing_fee')} />
                    <label htmlFor="evictFee">Eviction Filing Fee</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 5: Landlord Use & Tenant Termination */}
        <div className="card" style={styles.card}>
          <div className="card-body p-4">
            <div className="row g-4">
              {/* Landlord Use */}
              <div className="col-md-6 border-end">
                <h6 className="fw-bold mb-3 text-primary"><i className="bi bi-house-door me-2"></i>Landlord Personal Use</h6>
                <div className="row g-2">
                    <div className="col-6">
                        <div className="form-floating">
                            <input type="number" className="form-control" id="luNotice" {...register('landlord_use_notice_days', { required: true })} />
                            <label htmlFor="luNotice">Notice Days *</label>
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="form-floating">
                            <input type="number" className="form-control" id="luComp" {...register('landlord_use_compensation_months')} />
                            <label htmlFor="luComp">Comp (Months)</label>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="form-floating">
                            <input type="text" className="form-control" id="luForm" {...register('landlord_use_form_code')} />
                            <label htmlFor="luForm">Form Code (e.g. N12)</label>
                        </div>
                    </div>
                </div>
              </div>

              {/* Tenant Termination */}
              <div className="col-md-6">
                <h6 className="fw-bold mb-3 text-success"><i className="bi bi-person-walking me-2"></i>Tenant Termination</h6>
                 <div className="row g-2">
                    <div className="col-6">
                        <div className="form-floating">
                            <input type="number" className="form-control" id="ttNotice" {...register('tenant_termination_notice_days', { required: true })} />
                            <label htmlFor="ttNotice">Notice Days *</label>
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="form-floating">
                            <input type="text" className="form-control" id="ttForm" {...register('tenant_termination_form_code')} />
                            <label htmlFor="ttForm">Form Code</label>
                        </div>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 6: Lease, Service & Dispute */}
        <div className="card" style={styles.card}>
          <div className="card-body p-4">
            <h5 className="card-title mb-4" style={styles.sectionTitle}>
              <i className="bi bi-pen" style={styles.sectionIcon}></i>
              Lease, Service & Disputes
            </h5>
            
            <div className="row g-3">
              {/* Standard Lease */}
              <div className="col-md-12 bg-light p-3 rounded">
                <div className="row align-items-center">
                    <div className="col-md-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" {...register('has_standard_lease_form')} id="stdLease" />
                            <label className="form-check-label" htmlFor="stdLease">Standard Lease</label>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" {...register('lease_form_mandatory')} id="mandLease" />
                            <label className="form-check-label" htmlFor="mandLease">Mandatory?</label>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <input type="text" className="form-control" placeholder="Standard Lease Form Code" {...register('standard_lease_form_code')} />
                    </div>
                </div>
              </div>

              {/* Service Methods */}
              <div className="col-12 mt-3">
                <label className="form-label fw-bold small text-muted text-uppercase">Allowed Service Methods</label>
                <div className="d-flex flex-wrap gap-3">
                   {['Electronic', 'Registered Mail', 'In Person', 'Proof of Service'].map((label, idx) => {
                      const keys = ['allows_electronic_service', 'requires_registered_mail', 'allows_in_person_service', 'requires_proof_of_service'];
                      return (
                        <div className="form-check form-check-inline border px-3 py-2 rounded bg-white" key={idx}>
                           <input className="form-check-input" type="checkbox" {...register(keys[idx])} id={`srv_${idx}`} />
                           <label className="form-check-label ms-1" htmlFor={`srv_${idx}`}>{label}</label>
                        </div>
                      )
                   })}
                </div>
              </div>

              {/* Dispute Resolution */}
              <div className="col-12 mt-3">
                 <div className="row g-3">
                    <div className="col-md-4">
                        <div className="input-group">
                            <span className="input-group-text">Dispute Fee $</span>
                            <input type="number" step="0.01" className="form-control" {...register('dispute_filing_fee')} />
                        </div>
                    </div>
                    <div className="col-md-8">
                        <div className="input-group">
                            <div className="input-group-text bg-white">
                                <input className="form-check-input mt-0" type="checkbox" {...register('has_online_dispute_portal')} aria-label="Has Portal" />
                                <span className="ms-2">Online Portal</span>
                            </div>
                            <input type="text" className="form-control" {...register('dispute_portal_url')} placeholder="https://tribunalsontario.ca/..." />
                        </div>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end gap-2 my-4">
            <button type="button" className="btn btn-light btn-lg px-4" onClick={() => reset()}>Reset</button>
            <button type="submit" className="btn btn-primary btn-lg px-5 shadow" disabled={isLoading}>
                {isLoading ? (
                <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                </>
                ) : (
                <>
                    <i className="bi bi-check-circle-fill me-2"></i> Create Province
                </>
                )}
            </button>
        </div>

      </form>
    </div>
  )
}

export default CreateProvince